import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { Mic, AlertCircle, MicOff } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface VoiceSearchProps {
  onSearch: (query: string) => void;
  disabled?: boolean;
  // Square icon button with no "Voice" label — for compact contexts (the
  // chatbot's input row) where the labeled pill button doesn't fit well
  // alongside a text input and a send button.
  iconOnly?: boolean;
}

// Number of equalizer bars rendered in the listening overlay.
const BARS = 7;

export function VoiceSearch({ onSearch, disabled = false, iconOnly = false }: VoiceSearchProps) {
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
  } = useSpeechRecognition({ language: 'en-US' });

  // Tracks whether we were the ones who started this listening session, so the
  // submit-on-stop effect below doesn't fire from an unrelated recognition state.
  const sessionActiveRef = useRef(false);

  useEffect(() => {
    if (sessionActiveRef.current && !listening) {
      sessionActiveRef.current = false;
      // Chrome's recognizer auto-appends terminal punctuation (e.g. a trailing
      // "." or "?") to the final transcript that isn't part of what was said
      // and won't match listing keywords, so strip it before searching.
      const cleaned = transcript.trim().replace(/[.?!,]+$/, '');
      if (cleaned) {
        onSearch(cleaned);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening]);

  // Click to toggle, rather than press-and-hold: holding is fragile here because
  // the native mic-permission dialog steals window focus, which fires a
  // synthetic mouseup/mouseleave that would cancel listening before the user
  // can respond to the prompt.
  const startListening = () => {
    resetTranscript();
    sessionActiveRef.current = true;
    SpeechRecognition.startListening({ continuous: false, interimResults: true });
  };

  const stopListening = () => {
    SpeechRecognition.stopListening();
  };

  // The icon-only square is used in exactly one place — Niva's composer — and
  // is sized to match the text input beside it. The labelled pill keeps its
  // original height; it sits in the listings filter bar next to h-10 controls.
  const sizeClasses = iconOnly ? 'w-12 h-12 justify-center' : 'gap-2 px-3.5 h-10';
  const iconSize = iconOnly ? 20 : 18;

  if (!browserSupportsSpeechRecognition) {
    return (
      <button
        type="button"
        disabled
        className={`inline-flex items-center rounded-xl opacity-30 cursor-not-allowed text-muted-foreground ${sizeClasses}`}
        title="Voice search not supported in your browser"
        aria-label="Voice search not supported"
      >
        <AlertCircle size={iconSize} />
        {!iconOnly && <span className="text-sm font-semibold">Voice</span>}
      </button>
    );
  }

  // The browser has already denied mic access (via its own permission prompt).
  // We can't re-prompt from JS — the user must flip the site permission
  // manually (padlock icon in the address bar → Microphone → Allow) and reload.
  if (!isMicrophoneAvailable) {
    return (
      <button
        type="button"
        disabled
        className={`inline-flex items-center rounded-xl bg-destructive/10 text-destructive cursor-not-allowed ${sizeClasses}`}
        title="Microphone access is blocked. Click the padlock/site-info icon in your browser's address bar, allow Microphone, then reload the page."
        aria-label="Microphone access blocked"
      >
        <MicOff size={iconSize} />
        {!iconOnly && <span className="text-sm font-semibold">Voice</span>}
      </button>
    );
  }

  return (
    <>
      <button
        onClick={startListening}
        disabled={disabled}
        type="button"
        className={`inline-flex items-center rounded-xl border border-primary/30 bg-primary/10 text-primary font-semibold transition hover:bg-primary/15 ${sizeClasses} ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
        title="Click to speak"
        aria-label="Voice search"
      >
        <Mic size={iconSize} />
        {!iconOnly && <span className="text-sm">Voice</span>}
      </button>

      {listening && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Listening for voice search"
          onClick={stopListening}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background/80 backdrop-blur-md animate-in fade-in duration-200"
        >
          {/* Mic button with breathing glow + expanding rings */}
          <div className="relative flex items-center justify-center" style={{ width: 160, height: 160 }}>
            {/* Expanding rings */}
            <span
              className="absolute rounded-full border border-primary/60"
              style={{ width: 116, height: 116, animation: 'voice-ring 2s ease-out infinite' }}
            />
            <span
              className="absolute rounded-full border border-primary/50"
              style={{ width: 116, height: 116, animation: 'voice-ring 2s ease-out infinite', animationDelay: '1s' }}
            />
            {/* Breathing glow */}
            <span
              className="absolute rounded-full bg-primary/25 blur-xl"
              style={{ width: 132, height: 132, animation: 'voice-pulse 2s ease-in-out infinite' }}
            />
            {/* Mic disc */}
            <span
              className="relative flex items-center justify-center rounded-full bg-primary text-primary-foreground"
              style={{ width: 96, height: 96, boxShadow: '0 0 40px color-mix(in srgb, var(--color-primary) 50%, transparent)' }}
            >
              <Mic size={40} />
            </span>
          </div>

          {/* Equalizer bars */}
          <div className="mt-8 flex items-end gap-1.5" style={{ height: 40 }}>
            {Array.from({ length: BARS }).map((_, i) => {
              // Bars ripple outward from the centre for a symmetric wave.
              const distanceFromCenter = Math.abs(i - (BARS - 1) / 2);
              return (
                <span
                  key={i}
                  className="w-1.5 rounded-full bg-primary"
                  style={{
                    height: 40,
                    transformOrigin: 'bottom',
                    animation: 'voice-equalize 0.9s ease-in-out infinite',
                    animationDelay: `${distanceFromCenter * 0.12}s`,
                  }}
                />
              );
            })}
          </div>

          <h2
            className="mt-8 text-3xl font-bold text-foreground"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Listening…
          </h2>

          {/* Live transcript */}
          <p className="mt-3 min-h-[1.75rem] max-w-xl px-6 text-center text-lg italic text-muted-foreground">
            {transcript ? `“${transcript}”` : 'Speak now — describe what you’re looking for'}
          </p>

          <p className="mt-6 text-sm text-muted-foreground/70">Tap anywhere to stop</p>
        </div>,
        document.body,
      )}
    </>
  );
}
